import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Preloader } from 'konsta/react';
import { ArrowRight, Eye, PauseCircle, Pencil, UserCheck } from 'lucide-react';
import type { I18nKey } from '@harmony/shared';
import { api, type EditSuggestion, type MyProviderProfile } from '../../api';
import { EmptyState, PrimaryButton, useToast } from '../../components/ui';
import { ProfileBody } from '../ProviderProfile';
import { EditProviderPage } from '../EditProfile';
import { useNav } from '../../nav';
import { haptic } from '../../telegram';
import { useErrorText, useT } from '../../i18n';

/**
 * Provider's Profile tab: their own public profile straight away (as clients see it),
 * with anything that needs their attention on top and settings below.
 */
export function ProviderProfileTab({ settings }: { settings: ReactNode }) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const errText = useErrorText();
  const [p, setP] = useState<MyProviderProfile | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .myProviderProfile()
      .then((r) => {
        setP(r);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => {
    load();
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  if (!p) return failed ? <EmptyState icon="📡" text={t('error.generic')} /> : <div className="flex justify-center pt-24"><Preloader /></div>;

  const activate = async () => {
    setBusy(true);
    try {
      await api.setActive(true);
      haptic('success');
      toast(t('myprofile.activated'));
      load();
    } catch (e) {
      toast(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const inactiveText: I18nKey | null = p.isActive
    ? null
    : p.inactiveBy === 'AGENT'
      ? 'myprofile.inactive_by_agent'
      : p.inactiveBy === 'ADMIN'
        ? 'myprofile.inactive_by_admin'
        : 'myprofile.inactive_self';

  return (
    <div className="pb-28">
      {inactiveText && (
        <div className="mx-3 mt-3 rounded-2xl bg-amber-400/15 p-4">
          <div className="mb-2 flex items-start gap-2.5">
            <PauseCircle size={22} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-[15px]">{t(inactiveText)}</p>
          </div>
          {p.inactiveBy !== 'ADMIN' && (
            <PrimaryButton disabled={busy} onClick={activate}>
              {t('myprofile.activate')}
            </PrimaryButton>
          )}
        </div>
      )}

      {p.suggestions.map((s) => (
        <SuggestionCard key={s.id} s={s} onDone={load} />
      ))}

      {p.card ? (
        <>
          <div className="flex items-center gap-2 px-4 pb-2 pt-3">
            <span className="flex flex-1 items-center gap-1.5 text-sm text-tg-hint">
              <Eye size={15} /> {t('myprofile.preview')}
            </span>
            <button
              onClick={() => nav.push('edit-provider', <EditProviderPage onSaved={load} />)}
              className="flex items-center gap-1.5 rounded-full bg-tg-button px-4 py-2 text-sm font-semibold text-tg-buttonText active:opacity-80"
            >
              <Pencil size={15} /> {t('profile.edit')}
            </button>
          </div>
          <ProfileBody p={p.card} canBook={false} />
        </>
      ) : (
        <p className="m-4 rounded-2xl bg-tg-secondary p-4 text-tg-hint">{t('availability.pending')}</p>
      )}

      {p.agent && (
        <div className="mx-4 mt-6 flex items-center gap-3 rounded-2xl bg-tg-secondary p-3">
          <UserCheck size={22} className="text-tg-link" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-tg-hint">{t('reg.step_agent')}</div>
            <div className="truncate font-semibold">
              {p.agent.name} <span className="font-normal text-tg-hint">· {p.agent.agentCode}</span>
            </div>
          </div>
        </div>
      )}

      <h2 className="px-4 pb-1 pt-6 text-sm font-semibold text-tg-hint">{t('myprofile.settings')}</h2>
      {settings}
    </div>
  );
}

/** What the agent wants to change: "now → suggested", with Accept / Reject. */
function SuggestionCard({ s, onDone }: { s: MyProviderProfile['suggestions'][number]; onDone: () => void }) {
  const t = useT();
  const toast = useToast();
  const errText = useErrorText();
  const [busy, setBusy] = useState(false);
  const rows: Array<{ label: string; now: string; next: string }> = [];
  const c: EditSuggestion = s.changes;
  if (c.displayName !== undefined) rows.push({ label: t('edit.field.displayName'), now: s.current.displayName, next: c.displayName });
  if (c.bio !== undefined) rows.push({ label: t('edit.field.bio'), now: s.current.bio || '—', next: c.bio || '—' });
  for (const [m, price] of Object.entries(c.prices ?? {})) {
    rows.push({
      label: `${t('edit.field.prices')} · ${t('provider.min', { minutes: m })}`,
      now: s.current.prices[m] ? `${s.current.prices[m]} ETB` : '—',
      next: `${price} ETB`,
    });
  }
  const answer = async (a: 'accept' | 'reject') => {
    setBusy(true);
    try {
      await api.answerSuggestion(s.id, a);
      haptic('success');
      toast(t(a === 'accept' ? 'edit.accepted' : 'edit.rejected'));
      onDone();
    } catch (e) {
      toast(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-3 mt-3 rounded-2xl bg-tg-secondary p-4">
      <div className="mb-3 font-semibold">
        ✏️ {t('myprofile.edit_requests')} <span className="font-normal text-tg-hint">· {s.agentName}</span>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="text-xs font-semibold uppercase tracking-wide text-tg-hint">{r.label}</div>
            <div className="flex flex-wrap items-center gap-1.5 text-[15px]">
              <span className="text-tg-hint line-through">{r.now}</span>
              <ArrowRight size={14} className="text-tg-hint" />
              <span className="font-semibold">{r.next}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <PrimaryButton tone="soft" disabled={busy} onClick={() => answer('reject')}>
          {t('edit.reject')}
        </PrimaryButton>
        <PrimaryButton disabled={busy} onClick={() => answer('accept')}>
          {t('edit.accept')}
        </PrimaryButton>
      </div>
    </div>
  );
}
